// src/pages/Consultation/modals/ExchangeItemModal.jsx
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { FiX, FiUpload, FiTrash2, FiDollarSign } from "react-icons/fi";
import toast from "react-hot-toast";
import Button from "../../../../components/common/Button";
import { addExchangeItemThunk } from "../../../../store/thunks/consultationThunks";

const ITEM_TYPES = [
  { value: "GOLD", label: "Gold", color: "yellow" },
  { value: "SILVER", label: "Silver", color: "gray" },
  { value: "PLATINUM", label: "Platinum", color: "slate" },
  { value: "DIAMOND", label: "Diamond", color: "blue" },
  { value: "JEWELRY", label: "Jewelry", color: "purple" },
];

const METAL_PURITIES = {
  GOLD: ["24K", "22K", "18K", "14K", "10K"],
  SILVER: ["999", "925", "900", "800"],
  PLATINUM: ["PT950", "PT900", "PT850"],
};

const METAL_COLORS = {
  GOLD: ["Yellow", "White", "Rose"],
  SILVER: ["White", "Black"],
  PLATINUM: ["White", "Gray"],
};

const CONDITION_GRADES = [
  {
    value: "EXCELLENT",
    label: "Excellent",
    description: "Like new, minimal wear",
    color: "green",
  },
  {
    value: "GOOD",
    label: "Good",
    description: "Light wear, good condition",
    color: "blue",
  },
  {
    value: "FAIR",
    label: "Fair",
    description: "Visible wear, functional",
    color: "yellow",
  },
  {
    value: "POOR",
    label: "Poor",
    description: "Heavy wear, needs repair",
    color: "red",
  },
];

const ExchangeItemModal = ({ isOpen, onClose, session, adminInfo }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    itemType: "",
    description: "",
    brand: "",
    model: "",
    grossWeight: "",
    netWeight: "",
    stoneWeight: "",
    metalDetails: {
      purity: "",
      color: "",
      karat: "",
    },
    conditionGrade: "",
    conditionNotes: "",
  });

  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Handle field changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle nested metalDetails
    if (name.startsWith("metalDetails.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        metalDetails: { ...prev.metalDetails, [field]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    // Validate
    if (images.length + files.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }

    files.forEach((file) => {
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload only image files");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should not exceed 5MB");
        return;
      }

      // Add to images array
      setImages((prev) => [...prev, file]);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle image remove
  const handleImageRemove = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Auto-calculate net weight
  const calculateNetWeight = () => {
    const gross = parseFloat(formData.grossWeight) || 0;
    const stone = parseFloat(formData.stoneWeight) || 0;
    const net = gross - stone;

    if (net > 0) {
      setFormData((prev) => ({ ...prev, netWeight: net.toFixed(3) }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.itemType) {
      newErrors.itemType = "Please select item type";
    }

    if (!formData.description || formData.description.trim().length < 5) {
      newErrors.description = "Description is required (min 5 characters)";
    }

    if (!formData.grossWeight || formData.grossWeight <= 0) {
      newErrors.grossWeight = "Gross weight is required";
    }

    if (!formData.netWeight || formData.netWeight <= 0) {
      newErrors.netWeight = "Net weight is required";
    }

    if (
      ["GOLD", "SILVER", "PLATINUM"].includes(formData.itemType) &&
      !formData.metalDetails.purity
    ) {
      newErrors["metalDetails.purity"] = "Purity is required";
    }

    if (!formData.conditionGrade) {
      newErrors.conditionGrade = "Please select condition grade";
    }

    if (images.length === 0) {
      newErrors.images = "At least one image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);

    try {
      // Upload images first (you'll need an image upload API)
      const imageUrls = await uploadImages(images);

      const exchangeData = {
        sessionId: session._id,
        exchangeData: {
          itemType: formData.itemType,
          description: formData.description,
          brand: formData.brand || undefined,
          model: formData.model || undefined,
          grossWeight: parseFloat(formData.grossWeight),
          netWeight: parseFloat(formData.netWeight),
          stoneWeight: formData.stoneWeight
            ? parseFloat(formData.stoneWeight)
            : undefined,
          metalDetails:
            formData.metalDetails.purity || formData.metalDetails.color
              ? {
                  purity: formData.metalDetails.purity || undefined,
                  color: formData.metalDetails.color || undefined,
                  karat: formData.metalDetails.karat
                    ? parseInt(formData.metalDetails.karat)
                    : undefined,
                }
              : undefined,
          conditionGrade: formData.conditionGrade,
          conditionNotes: formData.conditionNotes || undefined,
          images: imageUrls,
        },
        adminCode: adminInfo.adminCode,
        adminName: adminInfo.adminName,
      };

      await dispatch(addExchangeItemThunk(exchangeData)).unwrap();

      toast.success("Exchange item added - System appraisal in progress");
      handleClose();
    } catch (error) {
      console.error("Add exchange item error:", error);
      toast.error(error || "Failed to add exchange item");
    } finally {
      setSubmitting(false);
    }
  };

  // Mock image upload (replace with actual API call)
  const uploadImages = async (files) => {
    // TODO: Implement actual image upload to your media service
    // For now, return mock URLs
    return files.map((file, i) => `exchange-item-${Date.now()}-${i}.jpg`);
  };

  // Handle close
  const handleClose = () => {
    if (!submitting) {
      setFormData({
        itemType: "",
        description: "",
        brand: "",
        model: "",
        grossWeight: "",
        netWeight: "",
        stoneWeight: "",
        metalDetails: {
          purity: "",
          color: "",
          karat: "",
        },
        conditionGrade: "",
        conditionNotes: "",
      });
      setImages([]);
      setImagePreviews([]);
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b z-10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FiDollarSign className="text-xl text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Add Exchange Item
                </h3>
                <p className="text-sm text-gray-500">
                  Session: {session?.sessionCode}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <FiX className="text-xl text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Item Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Item Type *
              </label>
              <div className="grid grid-cols-5 gap-3">
                {ITEM_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, itemType: type.value }))
                    }
                    disabled={submitting}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      formData.itemType === type.value
                        ? `border-${type.color}-500 bg-${type.color}-50`
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <p className="font-medium text-sm">{type.label}</p>
                  </button>
                ))}
              </div>
              {errors.itemType && (
                <p className="mt-2 text-sm text-red-500">{errors.itemType}</p>
              )}
            </div>

            {/* Basic Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder="e.g., Gold chain with pendant"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors.description ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.description}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand
                </label>
                <input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model
                </label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Weight Details */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-gray-900 mb-3">
                Weight Details (in grams)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gross Weight *
                  </label>
                  <input
                    type="number"
                    name="grossWeight"
                    value={formData.grossWeight}
                    onChange={handleChange}
                    onBlur={calculateNetWeight}
                    disabled={submitting}
                    step="0.001"
                    min="0"
                    placeholder="0.000"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.grossWeight ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.grossWeight && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.grossWeight}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stone Weight
                  </label>
                  <input
                    type="number"
                    name="stoneWeight"
                    value={formData.stoneWeight}
                    onChange={handleChange}
                    onBlur={calculateNetWeight}
                    disabled={submitting}
                    step="0.001"
                    min="0"
                    placeholder="0.000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Net Weight *
                  </label>
                  <input
                    type="number"
                    name="netWeight"
                    value={formData.netWeight}
                    onChange={handleChange}
                    disabled={submitting}
                    step="0.001"
                    min="0"
                    placeholder="0.000"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.netWeight ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.netWeight && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.netWeight}
                    </p>
                  )}
                </div>
              </div>
              <p className="text-xs text-blue-700 mt-2">
                Net Weight = Gross Weight - Stone Weight
              </p>
            </div>

            {/* Metal Details */}
            {["GOLD", "SILVER", "PLATINUM"].includes(formData.itemType) && (
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-medium text-gray-900 mb-3">
                  Metal Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Purity *
                    </label>
                    <select
                      name="metalDetails.purity"
                      value={formData.metalDetails.purity}
                      onChange={handleChange}
                      disabled={submitting}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                        errors["metalDetails.purity"]
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    >
                      <option value="">Select purity</option>
                      {METAL_PURITIES[formData.itemType]?.map((purity) => (
                        <option key={purity} value={purity}>
                          {purity}
                        </option>
                      ))}
                    </select>
                    {errors["metalDetails.purity"] && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors["metalDetails.purity"]}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Color
                    </label>
                    <select
                      name="metalDetails.color"
                      value={formData.metalDetails.color}
                      onChange={handleChange}
                      disabled={submitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select color</option>
                      {METAL_COLORS[formData.itemType]?.map((color) => (
                        <option key={color} value={color}>
                          {color}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.itemType === "GOLD" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Karat
                      </label>
                      <input
                        type="number"
                        name="metalDetails.karat"
                        value={formData.metalDetails.karat}
                        onChange={handleChange}
                        disabled={submitting}
                        min="10"
                        max="24"
                        placeholder="e.g., 22"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Condition Assessment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Condition Grade *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {CONDITION_GRADES.map((grade) => (
                  <button
                    key={grade.value}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        conditionGrade: grade.value,
                      }))
                    }
                    disabled={submitting}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      formData.conditionGrade === grade.value
                        ? `border-${grade.color}-500 bg-${grade.color}-50`
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <p className="font-medium text-sm">{grade.label}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {grade.description}
                    </p>
                  </button>
                ))}
              </div>
              {errors.conditionGrade && (
                <p className="mt-2 text-sm text-red-500">
                  {errors.conditionGrade}
                </p>
              )}
            </div>

            {/* Condition Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Condition Notes
              </label>
              <textarea
                name="conditionNotes"
                value={formData.conditionNotes}
                onChange={handleChange}
                disabled={submitting}
                rows={3}
                placeholder="Any scratches, damages, missing parts, etc..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Images * (Max 5)
              </label>

              {/* Image Previews */}
              {imagePreviews.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-3">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => handleImageRemove(index)}
                        disabled={submitting}
                        className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              {imagePreviews.length < 5 && (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors">
                  <FiUpload className="text-3xl text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">
                    Click to upload images
                  </span>
                  <span className="text-xs text-gray-400 mt-1">
                    {imagePreviews.length}/5 uploaded
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={submitting}
                    className="hidden"
                  />
                </label>
              )}

              {errors.images && (
                <p className="mt-2 text-sm text-red-500">{errors.images}</p>
              )}
            </div>

            {/* Info Box */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                After adding, the system will automatically appraise the item
                based on current market rates. You can adjust the value manually
                if needed.
              </p>
            </div>
          </form>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-white flex items-center justify-end gap-3 px-6 py-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting}
            >
              {submitting ? "Adding..." : "Add Exchange Item"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExchangeItemModal;
